import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProfileForm } from '@/components/profile/ProfileForm';
import { PasswordChangeForm } from '@/components/profile/PasswordChangeForm';
import { SubscriptionManagement } from '@/components/profile/SubscriptionManagement';
import { AccountSettings } from '@/components/profile/AccountSettings';

import { profileFormSchema, type ProfileFormValues } from '@/components/profile/ProfileFormSchema';
import { useOfflineProfile } from '@/contexts/offline';
import { usePermissions } from '@/contexts/unified-permissions';
import { useOfflineState } from '@/contexts/offline/useOfflineState';
import { LoadingState } from '@/components/ui/loading-state';
import { useIsMobile } from '@/hooks/use-mobile';
import { CircleHelp, WifiOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';


const Profile = () => {
  const { user } = usePermissions();
  const { profile, loading, saveProfile: saveProfileOffline } = useOfflineProfile();
  const { state } = useOfflineState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobile();
  const [helpOpen, setHelpOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const helpContent = {
    title: 'Profile Help',
    description: 'Manage your account settings and preferences',
    content: (
      <>
        <p>Manage your account and personalize your experience:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Update your personal information and contact details</li>
          <li>Set your belt rank and training preferences</li>
          <li>Manage notification settings</li>
          <li>View your subscription status and billing</li>
          <li>Connect with social media accounts</li>
        </ul>
        <p>Keep your profile up to date to get the most personalized training experience.</p>
      </>
    ),
  };

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      username: '',
      full_name: '',
      belt_rank: 'white',
      stripes: '0',
      home_gym: '',
      training_preference: 'Both',
      date_began_training: null,
      date_achieved_belt: null,
      email_preferences: {
        weekly_summary: true,
        weekly_insights: true,
        last_updated: new Date().toISOString()
      }
    },
  });

  // Enhanced profile data loading to handle ALL fields
  useEffect(() => {
    if (profile) {

      
      // Handle email_preferences jsonb structure
      let emailPreferences = {
        weekly_summary: true,
        weekly_insights: true,
        last_updated: new Date().toISOString()
      };

      if (profile.email_preferences) {
        try {
          // Handle both parsed JSON and string JSON
          const prefs = typeof profile.email_preferences === 'string' 
            ? JSON.parse(profile.email_preferences) 
            : profile.email_preferences;
          
          emailPreferences = {
            weekly_summary: prefs.weekly_summary ?? true,
            weekly_insights: prefs.weekly_insights ?? true,
            last_updated: prefs.last_updated || new Date().toISOString()
          };
        } catch (error) {
          console.error('Error parsing email preferences:', error);
        }
      }

      const formData: ProfileFormValues = {
        username: profile.username || '',
        full_name: profile.full_name || '',
        belt_rank: profile.belt_rank || 'white',
        stripes: profile.stripes || '0',
        home_gym: profile.home_gym || '',
        training_preference: 'Both', // Default value since not stored in offline schema
        date_began_training: null, // Default value since not stored in offline schema
        date_achieved_belt: null, // Default value since not stored in offline schema
        email_preferences: emailPreferences
      };

      console.log('Setting form data:', formData);
      form.reset(formData);
    }
  }, [profile, form]);

  const handleSubmit = async (data: ProfileFormValues, avatarUrl?: string | null) => {
    setIsSubmitting(true);
    try {
      console.log('Submitting profile data:', data);
      console.log('Avatar URL:', avatarUrl);

      await saveProfileOffline(data, avatarUrl ?? null);
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return <div>Please log in to view your profile.</div>;
  }

  if (loading) {
    return (
      <LoadingState 
        title="Loading Profile" 
        description="Getting your profile information..." 
        size="md"
        fullScreen={false}
      />
    );
  }

  return (
    <>
      {/* MainLayout handles MobileHeader, so no need for duplicate header */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {!isMobile && (
          <div className="flex items-center gap-2 mb-8">
            <h1 className="text-2xl font-bold">Profile Settings</h1>
            <CircleHelp
              className="h-6 w-6 text-primary cursor-pointer hover:text-primary/80 transition-colors"
              onClick={() => setHelpOpen(true)}
              strokeWidth={2}
            />
          </div>
        )}

        {!state.isOnline && activeTab === 'profile' && (
          <Alert className="mb-6">
            <WifiOff className="h-4 w-4" />
            <AlertDescription>
              You're currently offline. Profile changes will be saved locally and synced when you're back online.
            </AlertDescription>
          </Alert>
        )}

        {!state.isOnline && activeTab === 'subscription' && (
          <Alert className="mb-6">
            <WifiOff className="h-4 w-4" />
            <AlertDescription>
              You're currently offline. Subscription information is shown from cached data. Connect to the internet to manage your subscription or make changes.
            </AlertDescription>
          </Alert>
        )}







        <Tabs defaultValue="profile" className="w-full" onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="password">Password</TabsTrigger>
            <TabsTrigger value="subscription">Subscription</TabsTrigger>
            <TabsTrigger value="account">Account</TabsTrigger>
          </TabsList>
          
          <TabsContent value="profile">
            <ProfileForm
              form={form}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
            />
          </TabsContent>
          
          <TabsContent value="password">
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
              </CardHeader>
              <CardContent>
                {state.isOnline ? (
                  <PasswordChangeForm />
                ) : (
                  <div className="text-center py-8">
                    <WifiOff className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">Online Connection Required</h3>
                    <p className="text-muted-foreground mb-4">
                      Password changes require an active internet connection for security reasons.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Please connect to the internet to change your password.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="subscription">
            <SubscriptionManagement />
          </TabsContent>

          <TabsContent value="account">
            <AccountSettings />
          </TabsContent>
        </Tabs>

        {/* Help dialog for desktop */}
        {!isMobile && (
          <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{helpContent.title}</DialogTitle>
                <DialogDescription>{helpContent.description}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">{helpContent.content}</div>
              <div className="flex justify-end">
                <DialogClose asChild>
                  <Button variant="default" size="sm">
                    Got it
                  </Button>
                </DialogClose>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </>
  );
};

export default Profile;
